// client/src/pages/SellerStorefront.js
import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { Box, Typography, TextField, Button, Stack, Avatar, Alert } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { getImageUrl, API_BASE_URL } from '../utils/media';

const SellerStorefront = () => {
  const { token } = useContext(AuthContext);
  const [storeName, setStoreName] = useState('');
  const [about, setAbout] = useState('');
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE_URL}/api/seller/storefront`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!data) return;
        setStoreName(data.store_name);
        setAbout(data.about || '');
        if (data.banner_url) setBannerPreview(getImageUrl(data.banner_url));
      })
      .catch(() => {});
  }, [token]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setBannerFile(file);
    if (file) setBannerPreview(URL.createObjectURL(file));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      const data = new FormData();
      data.append('about', about);
      if (bannerFile) data.append('banner', bannerFile);
      const response = await fetch(`${API_BASE_URL}/api/seller/storefront`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body: data,
      });
      if (!response.ok) throw new Error('Failed to update storefront.');
      setMessage('Storefront updated!');
    } catch (err) {
      setMessage(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>Storefront</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Customize how your store page looks to shoppers.{storeName && (
          <> View it live at <Link to={`/sellers/${storeName}`}>/sellers/{storeName}</Link>.</>
        )}
      </Typography>
      {message && <Alert severity={message.includes('Failed') ? 'error' : 'success'} sx={{ mb: 2 }}>{message}</Alert>}
      <Stack component="form" onSubmit={handleSave} spacing={2} sx={{ maxWidth: 560 }}>
        {bannerPreview && (
          <Avatar src={bannerPreview} variant="rounded" sx={{ width: '100%', height: 140 }} />
        )}
        <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}>
          Upload Banner Image
          <input hidden type="file" accept="image/*" onChange={handleFileChange} />
        </Button>
        <TextField
          label="About Your Store" fullWidth multiline minRows={4}
          value={about} onChange={(e) => setAbout(e.target.value)}
          placeholder="Tell shoppers about your store — what you sell, your story, shipping info, etc."
        />
        <Button type="submit" variant="contained" disabled={saving} sx={{ alignSelf: 'flex-start' }}>
          {saving ? 'Saving...' : 'Save Storefront'}
        </Button>
      </Stack>
    </Box>
  );
};

export default SellerStorefront;
